import { Controller } from "@hotwired/stimulus"

// The report designer's Output group file/archive naming (TASK-225.4) —
// same two jobs as export_filename_controller (insert a token at the caret,
// keep a live server-resolved preview), kept as a SEPARATE controller rather
// than reusing that one: this one is keyed on a form + design
// (form_definition_id / branding_profile_id), not one export template's
// form_definition_version, because a report design cascades by form rather
// than by version. The preview is resolved server-side for the same reason
// export_filename_controller's is — the sanitising and blank-collapse rules
// would drift if reimplemented here.
export default class extends Controller {
  static targets = ["input", "preview"]
  static values = { url: String, formDefinitionId: String, brandingProfileId: String, layoutId: String }

  connect() {
    this.timers = {}
    this.inputTargets.forEach((input) => this.refresh(input.dataset.scope))
  }

  disconnect() {
    Object.values(this.timers).forEach((timer) => clearTimeout(timer))
  }

  insert(event) {
    event.preventDefault()

    const { token, scope } = event.currentTarget.dataset
    const input = this.inputFor(scope)
    if (!input) return

    const start = input.selectionStart ?? input.value.length
    const end = input.selectionEnd ?? input.value.length
    input.value = `${input.value.slice(0, start)}${token}${input.value.slice(end)}`

    const caret = start + token.length
    input.focus()
    input.setSelectionRange(caret, caret)

    this.refresh(scope)
  }

  changed(event) {
    this.refreshDebounced(event.currentTarget.dataset.scope)
  }

  // --- Private ---

  inputFor(scope) {
    return this.inputTargets.find((input) => input.dataset.scope === scope)
  }

  previewFor(scope) {
    return this.previewTargets.find((el) => el.dataset.scope === scope)
  }

  refreshDebounced(scope) {
    clearTimeout(this.timers[scope])
    this.timers[scope] = setTimeout(() => this.refresh(scope), 300)
  }

  async refresh(scope) {
    const input = this.inputFor(scope)
    const preview = this.previewFor(scope)
    // this.formDefinitionIdValue, not hasFormDefinitionIdValue: the QTAG
    // level always renders the data attribute (content_form_scope has no
    // form to pick there), just with an empty value — hasFormDefinitionIdValue
    // is true either way, so it never actually guarded against a blank id.
    // An empty id was reaching the server and landing on the WRONG branch
    // of ExportFilenamePreviewsController#show (the export-template path,
    // keyed on a version id this request never sends), 404ing every refresh
    // at that level.
    if (!input || !preview || !this.hasUrlValue || !this.formDefinitionIdValue) return

    const params = new URLSearchParams({
      pattern: input.value,
      scope: scope,
      form_definition_id: this.formDefinitionIdValue,
      branding_profile_id: this.brandingProfileIdValue
    })
    // Spreadsheet mode (TASK-229.5) keys {template} off which source is being
    // named — "main" or a layout id — so its two patterns (file/zip) send it;
    // the PDF rail never sets this value, so its own preview is untouched.
    if (this.hasLayoutIdValue) params.set("layout_id", this.layoutIdValue)

    try {
      const response = await fetch(`${this.urlValue}?${params}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const data = await response.json()
      preview.textContent = data.preview
    } catch {
      // A preview is a convenience, never a blocker — leave the last good one.
    }
  }
}
